import { parseDbTimestamp } from "@repo/shared";
import {
  type ClientActivityEvent,
  type ClientCall,
  type ClientMeeting,
  type ClientNote,
  isFlowInstanceEditable,
  isUpcomingMeeting,
  type Translated,
} from "@repo/shared/types";
import { CalendarClock, ChevronDown, Phone, StickyNote, Users, Workflow } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { SectionHeader } from "@/components/ui/section-header";
import {
  rowControl,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowLink,
} from "@/components/ui/table";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { displayUserName } from "@/lib/user-display";
import { getRelativeTime, localCalendarDayOffset } from "@/lib/utils";
import { T } from "./activity-section.i18n";
import { recordKindIcon } from "./notes-and-files";

type ActivityT = Translated<typeof T>;

type ActivityKind = "note" | "call" | "meeting" | "flow";

type ActivityItem = {
  id: string;
  entityId: string;
  kind: ActivityKind;
  date: string;
  topic: string;
  summary: string;
  by: string;
  href?: string;
};

export type ActivityOpenTarget = "note" | "call" | "meeting";

const PAST_ACTIVITY_LIMIT = 20;

function getRelativeDay(iso: string, t: ActivityT): string {
  const diffDays = localCalendarDayOffset(iso);
  if (diffDays === null) return "";
  if (diffDays === 0) return t.today;
  if (diffDays === -1) return t.yesterday;
  if (diffDays === 1) return t.tomorrow;
  if (diffDays < 0) return t.daysAgo(-diffDays);
  return t.inDays(diffDays);
}

// Same glyphs as the Notes & files tab (QL-125), plus the flow one.
function kindIcon(kind: ActivityKind) {
  if (kind === "flow") return <Workflow className="h-4 w-4" />;
  return recordKindIcon(kind);
}

export type ActivityCreateTarget = "flow" | "meeting" | "call" | "note";

type AuditUser = { name: string; deletedAt?: string | null };

/** The last person to touch an entry, with "(deleted)" when they were deleted (QL-365). */
function lastEditor(
  entry: { createdBy?: AuditUser; updatedBy?: AuditUser },
  language: string,
): string {
  const user = entry.updatedBy?.name ? entry.updatedBy : entry.createdBy;
  return displayUserName(user, language) ?? "";
}

export function ActivitySection({
  clientId,
  notes,
  calls,
  meetings,
  events,
  onCreate,
  onOpen,
}: {
  clientId: string;
  notes: ClientNote[];
  calls: ClientCall[];
  meetings: ClientMeeting[];
  events: ClientActivityEvent[];
  onCreate: (target: ActivityCreateTarget) => void;
  onOpen: (target: ActivityOpenTarget, id: string) => void;
}) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);

  const upcomingMeetings = useMemo(() => {
    const now = new Date();
    // occurredAt is zone-less UTC text: sort with the filter's parser; never compare as strings.
    return meetings
      .filter((m) => isUpcomingMeeting(m.occurredAt, now))
      .sort(
        (a, b) =>
          (parseDbTimestamp(a.occurredAt)?.getTime() ?? 0) -
          (parseDbTimestamp(b.occurredAt)?.getTime() ?? 0),
      );
  }, [meetings]);

  const pastActivity: ActivityItem[] = useMemo(() => {
    const now = new Date();
    const items: ActivityItem[] = [];

    for (const m of meetings) {
      if (isUpcomingMeeting(m.occurredAt, now)) continue;
      items.push({
        id: `meeting-${m.id}`,
        entityId: m.id,
        kind: "meeting",
        date: String(m.updatedAt),
        topic: m.topic,
        summary: m.summary || m.content || "",
        by: lastEditor(m, userLanguage),
      });
    }
    for (const c of calls) {
      items.push({
        id: `call-${c.id}`,
        entityId: c.id,
        kind: "call",
        date: String(c.updatedAt),
        topic: c.topic,
        summary: c.summary || c.content || "",
        by: lastEditor(c, userLanguage),
      });
    }
    for (const n of notes) {
      items.push({
        id: `note-${n.id}`,
        entityId: n.id,
        kind: "note",
        date: String(n.updatedAt),
        topic: n.title,
        summary: n.summary || n.content || "",
        by: lastEditor(n, userLanguage),
      });
    }
    // Flow lifecycle events from the process audit log (QL-275) — one row per
    // event that actually happened. Type stays "Flow"; the event word goes in
    // the Event column.
    const eventLabels: Record<ClientActivityEvent["type"], string> = {
      flow_created: t.created,
      flow_submitted: t.submitted,
      flow_unlocked: t.unlocked,
      flow_drafted: t.drafted,
      flow_reopened: t.reopened,
      flow_cancelled: t.cancelled,
      pdf_generated: t.pdfGenerated,
      signature_signed: t.signed,
      flow_completed: t.completed,
    };
    for (const e of events) {
      items.push({
        id: e.id,
        entityId: e.flowInstanceId,
        kind: "flow",
        date: e.timestamp,
        topic: e.flowName || t.flow,
        summary: eventLabels[e.type],
        by: displayUserName({ name: e.actorName, deletedAt: e.actorDeletedAt }, userLanguage) ?? "",
        // QL-274 — link by the instance's CURRENT status: editable → wizard,
        // post-submission/cancelled → process page.
        href: isFlowInstanceEditable(e.flowStatus)
          ? `/clients/${clientId}/flows/${e.flowInstanceId}`
          : `/clients/${clientId}/flows/${e.flowInstanceId}/process`,
      });
    }

    return items.sort((a, b) => b.date.localeCompare(a.date)).slice(0, PAST_ACTIVITY_LIMIT);
  }, [meetings, calls, notes, events, clientId, t, userLanguage]);

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <SectionHeader
          title={
            <span className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              {t.upcoming}
            </span>
          }
          action={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  {t.new}
                  <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onCreate("flow")}>
                  <Workflow className="h-4 w-4 mr-2" />
                  {t.newFlow}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onCreate("note")}>
                  <StickyNote className="h-4 w-4 mr-2" />
                  {t.newNote}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onCreate("call")}>
                  <Phone className="h-4 w-4 mr-2" />
                  {t.newCall}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onCreate("meeting")}>
                  <Users className="h-4 w-4 mr-2" />
                  {t.newMeeting}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
        {upcomingMeetings.length === 0 ? (
          <EmptyState>{t.upcomingNone}</EmptyState>
        ) : (
          <Table variant="interactive">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">{t.columnDate}</TableHead>
                <TableHead className="w-[100px]">{t.columnType}</TableHead>
                <TableHead className="w-[260px]">{t.columnTopic}</TableHead>
                <TableHead>{t.columnSummary}</TableHead>
                <TableHead className="w-[160px]">{t.columnBy}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingMeetings.map((m) => (
                <TableRow key={m.id} onClick={() => onOpen("meeting", m.id)}>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {getRelativeDay(m.occurredAt, t)}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {t.meeting}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium truncate max-w-[260px]">{m.topic}</TableCell>
                  <TableCell className="text-muted-foreground truncate max-w-0">
                    <span className="block truncate">{m.summary || m.content || t.noContent}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground truncate max-w-[160px]">
                    {displayUserName(m.createdBy, userLanguage)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="space-y-4">
        <SectionHeader title={t.pastTitle} />
        {pastActivity.length === 0 ? (
          <EmptyState>{t.pastNone}</EmptyState>
        ) : (
          <Table variant="interactive">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">{t.columnUpdated}</TableHead>
                <TableHead className="w-[100px]">{t.columnType}</TableHead>
                <TableHead className="w-[260px]">{t.columnSubject}</TableHead>
                <TableHead>{t.columnEvent}</TableHead>
                <TableHead className="w-[160px]">{t.columnBy}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pastActivity.map((item) => {
                const labelByKind: Record<ActivityKind, string> = {
                  meeting: t.meeting,
                  call: t.call,
                  // Flow events keep the "Flow" type label — the action is in Event.
                  flow: t.flow,
                  note: t.note,
                };
                const label = labelByKind[item.kind];
                // Only content-bearing entities (note/call/meeting) fall back to
                // "No content"; event rows (completed/signed/pdf) leave it blank.
                const hasContentKind =
                  item.kind === "note" || item.kind === "call" || item.kind === "meeting";
                const summaryText = item.summary || (hasContentKind ? t.noContent : "");
                const dateText = getRelativeTime(item.date, userLanguage);
                // Rows with an href navigate, so they are real row links (Cmd/Ctrl-click
                // opens a new tab); the rest open their entity in-page.
                const href = item.href;
                return (
                  <TableRow
                    key={item.id}
                    onClick={
                      href
                        ? undefined
                        : () => onOpen(item.kind as ActivityOpenTarget, item.entityId)
                    }
                  >
                    <TableCell className="text-muted-foreground tabular-nums">
                      <span className={rowControl}>
                        <TimeAgoTooltip date={parseDbTimestamp(item.date)}>
                          {dateText}
                        </TimeAgoTooltip>
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        {kindIcon(item.kind)}
                        {label}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium truncate max-w-[260px]">
                      {href ? <TableRowLink to={href}>{item.topic}</TableRowLink> : item.topic}
                    </TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-0">
                      <span className="block truncate">{summaryText}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-[160px]">
                      {item.by}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
